import { CreateTicketForm, Pool, TicketBody, TicketDetailsParams } from 'features/PunchupPages/TicketDetails/types';
import {
	createBody,
	defaultValues,
	fillForm,
	getTicketTypeCanGoLive,
	resolveTicketImage,
	showReqFieldsModal,
	validationSchema,
} from 'features/PunchupPages/TicketDetails/utils';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { TicketTypeDetailsForm } from 'features/PunchupPages/TicketDetails/components/TicketTypeDetailsForm';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { RequiredFieldsModal } from 'features/Embed/components/RequiredFields/RequieredFieldsModal';
import { ReqFieldsProps } from 'features/PunchupPages/TicketDetails/types';
import { camelizeKeys } from 'humps';
import { createTicket } from 'features/CreateTicket/services';
import { getData } from 'services/api';
import { uploadImage } from 'services/UploadImage/uploadImage';
import { addTicketTypeToPool, getEventPools } from 'features/PunchupPages/TicketDetails/services';
import { queryClient } from 'index';
import { updateTicket } from 'features/PunchupPages/TicketDetails/services';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { typedYupResolver } from 'features/common/utils/typedYupResolver';
import { camelize, sendGAEvent } from 'utils/utils';
import { EventCanGoLive } from 'features/PunchupPages/TicketDetails/types';
import { Form } from 'features/common/components/Form';
import { FlexContainer } from 'features/common/components/FlexContainer';
import useGetEvent from 'features/common/hooks/useGetEvent';
import {
	useAccordionForm,
	useSeededEditForm,
} from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';
import { NoPoolConfirmDialog } from 'features/PunchupPages/TicketDetails/components/NoPoolConfirmDialog';
import { shouldWarnNoPool } from 'features/common/utils/poolGuards';
import { getTagId } from 'features/common/utils/tagUtils';
import {
	addTagsToTicketType,
	removeTagsFromTicketType,
} from 'features/Dashboard/components/tixologist/TagsSection/services';
import { useDeleteTicket } from '../useTicketTypes';

// ── Types ─────────────────────────────────────────────────────────────────────

// Extends the shared form type with tag_ids so TicketTagsField can detect staged mode.
// tag_ids is not submitted to the ticket API — it is consumed by this form's onSuccess
// to diff and apply add/remove operations after the ticket save completes.
type TicketTypeFormValues = CreateTicketForm & { tag_ids: number[] };

interface TicketTypeFormProps {
	/** null → create mode; string → edit mode for this ticket ID */
	ticketId: string | null;
	onDone: () => void;
}

/**
 * TicketTypeForm
 *
 * Renders the full ticket create/edit form inline inside a PunchupAccordionSection,
 * without navigating to a sub-page. Uses the same TicketTypeDetailsForm component as
 * PunchupTicketDetails but accepts an explicit ticketId prop instead of URL params.
 *
 * Tag assignments are staged in the `tag_ids` form field and only persisted to the API
 * when the form's Save button is submitted (transactional flow). TicketTagsField detects
 * the presence of `tag_ids` in the form context and automatically switches to staged mode.
 */
export const TicketTypeForm: React.FC<TicketTypeFormProps> = ({ ticketId, onDone }) => {
	const { t } = useTranslation();
	const { id: eventId } = useParams<TicketDetailsParams>();
	const { event } = useGetEvent();

	const isNewTicket = ticketId === null;

	const [showReqFields, setShowReqFields] = useState(false);
	const [reqFieldsProps, setReqFieldsProps] = useState<ReqFieldsProps>();
	const [showNoPoolConfirm, setShowNoPoolConfirm] = useState(false);

	// Quantity floor (already-sold), read at validation time so it reflects the loaded ticket.
	const soldFloorRef = useRef(0);

	const formMethods = useForm<TicketTypeFormValues>({
		defaultValues: { ...defaultValues, tag_ids: [] },
		resolver: typedYupResolver(validationSchema(t, () => soldFloorRef.current)),
	});

	const { flash } = useAccordionForm(formMethods);
	const { isSubmitting } = formMethods.formState;
	const { deleteTicket } = useDeleteTicket();

	// Tracks the tag baseline so we can diff on save and apply the minimum add/remove calls.
	const initialTagIdsRef = useRef<number[]>([]);
	// Tracks the ticket ID used in the most recent submit (needed in onSuccess when ticketId prop is null).
	const submittingTicketIdRef = useRef<number | undefined>(undefined);

	// ── Data fetching ───────────────────────────────────────────────

	const { data: ticket } = useQuery<TicketBody>({
		queryKey: ['ticket', ticketId],
		queryFn: () => getData(`/public/ticketing/ticket_types/${ticketId}`),
		enabled: !isNewTicket,
	});

	// Keep the quantity floor current for the validation getter above.
	soldFloorRef.current = ticket?.sold ?? 0;

	const { data: canGoLive } = useQuery<EventCanGoLive>({
		queryKey: ['canGoLive', eventId],
		queryFn: () => {
			const searchParams = new URLSearchParams([['set_live', 'false']]);
			return getData(`/events/${eventId}/go-live?` + searchParams.toString());
		},
		select: (data) => camelizeKeys(data) as EventCanGoLive,
		enabled: !!eventId,
	});

	const { data: pools = [] } = useQuery({
		queryKey: ['eventPools', eventId],
		queryFn: (): Promise<Pool[]> => getEventPools(Number(eventId)),
		enabled: !!eventId,
		select: (data) => camelize(data) as Pool[],
	});

	// ── Form initialisation ─────────────────────────────────────────

	// Seed the form once from the saved ticket (edit mode), held behind a spinner until then so the
	// toggles/date pickers don't paint defaults and then re-seed (the blink). A later refetch must NOT
	// re-reset and clobber in-progress edits — the hook guards that. Create mode renders immediately.
	const { isSeeded, seedGate } = useSeededEditForm({
		isEditMode: !isNewTicket,
		formMethods,
		seedData: event && ticket ? { ticket, event } : undefined,
		toFormValues: ({ ticket, event }) => {
			const filled = fillForm(ticket, event.timeZone) ?? defaultValues;
			return { ...filled, tag_ids: ticket.tags?.map(getTagId) ?? [] } as TicketTypeFormValues;
		},
		onSeeded: ({ ticket }) => {
			initialTagIdsRef.current = ticket.tags?.map(getTagId) ?? [];
		},
	});

	// ── Tag diff helpers ────────────────────────────────────────────

	const applyTagDiff = async (ticketTypeId: number, currentTagIds: number[]) => {
		const toAdd = currentTagIds.filter((id) => !initialTagIdsRef.current.includes(id));
		const toRemove = initialTagIdsRef.current.filter((id) => !currentTagIds.includes(id));
		if (toAdd.length) await addTagsToTicketType(ticketTypeId, { tag_ids: toAdd });
		if (toRemove.length) await removeTagsFromTicketType(ticketTypeId, { tag_ids: toRemove });
		initialTagIdsRef.current = currentTagIds;
	};

	// ── Mutations ───────────────────────────────────────────────────

	const { mutateAsync: mutateCreateTicket } = useMutation(createTicket, {
		onError: () => flash('error', { title: t('ticketType-save-failed', 'Failed to save ticket type') }),
		onSuccess: async (response: any) => {
			sendGAEvent({ category: 'Journeys', action: 'Create ticket' });
			const body = response?.data;
			const created = Array.isArray(body) ? body[0] : body;
			const newId: number | undefined = created?.ID ?? created?.id;
			if (newId) {
				const stagedTagIds = (formMethods.getValues('tag_ids') as number[] | undefined) ?? [];
				if (stagedTagIds.length > 0) {
					try {
						await addTagsToTicketType(newId, { tag_ids: stagedTagIds });
					} catch {
						// Tag assignment failure is non-fatal; the ticket itself was saved.
					}
				}
				// The batch ticket-create POST does not persist the pool FK, so attach membership via the pool
				// endpoint (the path that works) when a pool was chosen. Non-fatal, like tags — the ticket saved.
				const chosenPoolId = Number(formMethods.getValues('poolId'));
				if (chosenPoolId > 0) {
					try {
						await addTicketTypeToPool(Number(eventId), chosenPoolId, newId);
					} catch {
						// Pool attach failure is non-fatal; the manager can attach it from the Pools accordion.
					}
				}
				initialTagIdsRef.current = stagedTagIds;
				submittingTicketIdRef.current = newId;
			}
			flash('success', {
				title: t('ticketType-saved', 'Ticket type saved'),
				onComplete: async () => {
					await queryClient.invalidateQueries({ queryKey: ['ticketTypes', eventId] });
					await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
					// Membership may have changed via the pool attach above — refresh the pool tile/analytics.
					await queryClient.invalidateQueries({ queryKey: ['eventPools', eventId] });
					onDone();
				},
			});
		},
	});

	const { mutateAsync: mutateUpdateTicket } = useMutation(updateTicket, {
		onError: () => flash('error', { title: t('ticketType-save-failed', 'Failed to save ticket type') }),
		onSuccess: async () => {
			const ticketTypeId = submittingTicketIdRef.current;
			if (ticketTypeId) {
				const currentTagIds = (formMethods.getValues('tag_ids') as number[] | undefined) ?? [];
				try {
					await applyTagDiff(ticketTypeId, currentTagIds);
				} catch {
					// Tag operation failure is non-fatal; the ticket itself was saved.
				}
			}
			sendGAEvent({ category: 'Journeys', action: 'Edit ticket' });
			flash('success', {
				title: t('ticketType-saved', 'Ticket type saved'),
				onComplete: async () => {
					await queryClient.invalidateQueries({ queryKey: ['ticketTypes', eventId] });
					await queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
					await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
					onDone();
				},
			});
		},
	});

	// Upload mutation for the deferred ticket image — declared after the create/update
	// mutations so the hook call order stays stable across renders.
	const { mutateAsync: mutateUploadImage } = useMutation(uploadImage, {
		onError: () => flash('error', { title: t('error-uploading-image', 'Failed to upload image') }),
	});

	// ── Submit ──────────────────────────────────────────────────────

	// The actual save — reached directly, or after the "no pool" warning is confirmed.
	const proceedSubmit = async () => {
		const resolvedId = ticketId ? Number(ticketId) : undefined;
		submittingTicketIdRef.current = resolvedId;

		try {
			const values = formMethods.getValues();
			const { image, displayType } = await resolveTicketImage(values, mutateUploadImage, Number(eventId));
			const body = createBody({ ...values, image, displayType }, Number(eventId), event?.timeZone || '', resolvedId);
			if (isNewTicket) await mutateCreateTicket(body);
			else await mutateUpdateTicket(body);
		} catch {
			// Upload / create / update errors are flashed by their own onError handlers.
		}
	};

	const onSubmit = async (currentValues: TicketTypeFormValues) => {
		if (event && canGoLive && showReqFieldsModal(event, canGoLive, currentValues.initialPrice || 0)) {
			setReqFieldsProps({
				eventGoLive: getTicketTypeCanGoLive(canGoLive, ticket?.ID),
				eventId: Number(eventId),
				type: 'ticket',
				closeModal: () => setShowReqFields(false),
			});
			setShowReqFields(true);
			return;
		}

		// Warn once if a brand-new tier joins no pool while the show has pools (T-5).
		if (shouldWarnNoPool(isNewTicket, pools.length, currentValues.poolId)) {
			setShowNoPoolConfirm(true);
			return;
		}

		await proceedSubmit();
	};

	// ── Render ──────────────────────────────────────────────────────

	const handleDelete = ticketId
		? async () => {
				try {
					await deleteTicket(Number(ticketId));
					onDone();
				} catch {
					// error handled by useDeleteTicket onError
				}
		  }
		: undefined;

	// Edit mode is held behind the spinner until the saved ticket is seeded (create renders immediately).
	if (!isSeeded) return <>{seedGate}</>;

	return (
		<Form formMethods={formMethods} onSubmit={onSubmit}>
			<div className="entityEditBody">
				<TicketTypeDetailsForm
					isNewTicket={isNewTicket}
					resolvedTicketId={ticketId ? Number(ticketId) : undefined}
					ticketTags={ticket?.tags}
					soldCount={ticket?.sold ?? 0}
					onDelete={handleDelete}
				/>

				<FlexContainer justifyContent="flex-end" gap="12px" className="entityEditActions">
					<PunchupButton type="button" variant="secondary" onClick={onDone} text={t('button-cancel')} />
					<PunchupButton
						type="submit"
						variant="primary"
						disabled={isSubmitting}
						isLoading={isSubmitting}
						text={isNewTicket ? t('button-create-new') : t('button-save')}
					/>
				</FlexContainer>

				{showReqFields && reqFieldsProps && <RequiredFieldsModal {...reqFieldsProps} />}

				{showNoPoolConfirm && (
					<NoPoolConfirmDialog
						onBack={() => setShowNoPoolConfirm(false)}
						onContinue={() => {
							setShowNoPoolConfirm(false);
							proceedSubmit();
						}}
					/>
				)}
			</div>
		</Form>
	);
};
